import { zodResolver } from '@hookform/resolvers/zod';
import moment from 'moment';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useRouter } from 'shared-routing';
import { cardAspectRatio, Recommendations, SpaDayDetailTemplate } from 'voyager';

import { BasketDrawerVersion } from '@common/components/BasketDrawer';
import { ContentParser } from '@common/components/ContentParser';
import { Spinner } from '@common/components/Spinner';
import { Routes } from '@common/constants';
import { normaliseGenderCode } from '@common/helpers/genderRestriction';
import { useCanonicalBookingStep, useLoadingState } from '@common/hooks';
import { JourneyPickerV2Connected } from '@features/JourneyPickerV2/container';
import { openJourneyPickerDates } from '@features/JourneyPickerV2/openJourneyPickerDates';
import { useCategoryNavigation } from '@features/JourneyPickerV2/useCategoryNavigation';
import { useNavCategories } from '@features/JourneyPickerV2/useNavCategories';
import {
  useGetSpaDaysPage,
  useGetSpaPackage,
  useGetSpaPackageTimeslots,
  useGetSpaSettings,
} from '@features/spa/hooks';
import { useGetSpaPackageExtras } from '@features/spa/hooks/useGetSpaPackageExtras';
import { useTranslations } from '@lang';
import { mergeCrossSellWithUpsell, parseDateToString } from '@utils/helpers';
import { isIntegrationUnavailable } from '@utils/integrationErrorCode';
import { mapDiscountToDisplayObject } from '@utils/mapDiscountToDisplayObject';
import {
  mapSpaCrossSellToRecommendations,
  SpaCrossSellEntry,
} from '@utils/mapSpaCrossSellToRecommendations';
import { SpaPackageType } from 'shared-types/SpaPackage';

import { SpaPackageFormValues, buildSpaPackageFormSchema } from '../SpaPackageForm.schema';
import { buildInitialConfigurationGroups, clampGuestCountToPackage } from './SpaDayBooking.helpers';
import { SpaDayBooking } from './SpaDayBooking';
import { SpaDayExtras } from './SpaDayExtras';
import { SpaDayV2Props } from './SpaDayV2.types';
import { useSpaPackageDateAvailability } from './hooks/useSpaPackageDateAvailability';

export const SpaDayV2: FC<SpaDayV2Props> = ({
  id,
  property,
  language,
  bookingStep: requestedBookingStep,
  loggedInGuest,
  journeyParams,
}) => {
  const router = useRouter();
  const { t } = useTranslations();
  const bookingStep = useCanonicalBookingStep(requestedBookingStep, {
    entity: 'spaDay',
    bookingRoute: Routes.SPA_DAY_BOOKING,
    detailRoute: Routes.SPA_DAY_DETAIL,
    id,
  });
  const [isBasketDrawerOpen, setIsBasketDrawerOpen] = useState(false);
  const startDate = moment(journeyParams?.spaDate).format('YYYY-MM-DD');

  const navCategories = useNavCategories();
  const handleNavCategoryChange = useCategoryNavigation();

  const journeyBar = (
    <JourneyPickerV2Connected
      imageOverlay
      dateMode="single"
      dateContext="spa"
      showGuests
      numberOfMonths={1}
      categories={navCategories}
      category="spa-days"
      onCategoryChange={handleNavCategoryChange}
      disableCategorySelect
    />
  );

  const { data: spaPackage, isFetching } = useGetSpaPackage(property.id, id, language, false);

  const { isInitialLoading, isRefetching } = useLoadingState(isFetching, !!spaPackage);

  const { data: spaSettings } = useGetSpaSettings(property.id);
  const { data: spaDaysPage } = useGetSpaDaysPage(property.id, language);
  const fallbackImageUrl = spaDaysPage?.heroBackgroundImage?.url;

  const entity = spaPackage as SpaPackageType | undefined;

  const selectedDateString = moment(journeyParams?.spaDate).format('YYYY-MM-DD');

  const {
    data: timeslots,
    isFetching: isLoadingTimeslots,
    isError: isTimeslotsError,
    error: timeslotsError,
    refetch: refetchTimeslots,
  } = useGetSpaPackageTimeslots(
    property.id,
    id,
    selectedDateString,
    entity?.numberOfGuests ?? 1,
    1,
    !!entity
  );

  const isTelephoneOnly = entity?.telephoneOnly || false;
  const supportsTimeslots = !!entity?.supportsTimeslots;

  const { isUnavailableForDate, isCheckingAvailability } = useSpaPackageDateAvailability({
    propertyId: property.id,
    packageId: id,
    date: selectedDateString,
    guests: journeyParams?.spaAdults,
    language,
    enabled: !!entity && !isTelephoneOnly && !supportsTimeslots,
  });

  const isUnavailable =
    !isTelephoneOnly &&
    (supportsTimeslots
      ? !isLoadingTimeslots &&
        (isTimeslotsError || (!!timeslots && !timeslots[selectedDateString]?.length))
      : isUnavailableForDate);

  // Distinct from "no availability": the read failed, so nothing is known either way. Only the
  // timeslot branch can be unknown — the date branch guards on data, so a failure there leaves
  // isUnavailable false rather than wrong.
  const integrationUnavailable = useMemo(
    () => supportsTimeslots && isIntegrationUnavailable(timeslotsError),
    [supportsTimeslots, timeslotsError]
  );

  const { data: extras = [] } = useGetSpaPackageExtras(
    property.id,
    id,
    language,
    selectedDateString
  );

  const genderRestriction = normaliseGenderCode(entity?.gender);
  const defaultGender =
    genderRestriction === 'M' ? 'male' : genderRestriction === 'F' ? 'female' : '';

  const initialGuestCount = entity
    ? clampGuestCountToPackage(entity, journeyParams?.spaAdults)
    : journeyParams?.spaAdults || 1;

  const form = useForm<SpaPackageFormValues>({
    resolver: entity ? zodResolver(buildSpaPackageFormSchema(entity)) : undefined,
    defaultValues: {
      date: selectedDateString,
      guests: Array.from({ length: initialGuestCount }, () => ({
        title: '',
        firstName: '',
        lastName: '',
        gender: defaultGender,
      })),
    },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  useEffect(() => {
    if (!entity) return;
    const target = clampGuestCountToPackage(entity, journeyParams?.spaAdults);
    const currentGuests = form.getValues('guests') || [];
    if (currentGuests.length === target) return;
    const nextGuests =
      currentGuests.length > target
        ? currentGuests.slice(0, target)
        : [
            ...currentGuests,
            ...Array.from({ length: target - currentGuests.length }, () => ({
              title: '',
              firstName: '',
              lastName: '',
              gender: defaultGender,
            })),
          ];
    form.setValue('guests', nextGuests as SpaPackageFormValues['guests'], {
      shouldDirty: true,
    });
  }, [entity, journeyParams?.spaAdults, defaultGender, form]);

  useEffect(() => {
    if (form.getValues('date') !== selectedDateString) {
      form.setValue('date', selectedDateString);
    }
  }, [selectedDateString, form]);

  useEffect(() => {
    if (!entity) return;
    const currentGuests = form.getValues('guests') || [];
    if (!currentGuests.some((g) => !g.configurationGroups)) return;
    form.setValue(
      'guests',
      currentGuests.map((g) =>
        g.configurationGroups
          ? g
          : {
              ...g,
              configurationGroups: buildInitialConfigurationGroups(entity.configurationGroups),
            }
      )
    );
  }, [entity, form]);

  const handleCloseModal = useCallback(
    (showMiniBasket = false) => {
      router.push({ pathname: Routes.SPA_DAY_DETAIL, query: { id } });
      if (showMiniBasket) {
        setIsBasketDrawerOpen(true);
      }
    },
    [router, id]
  );

  const handleReserve = useCallback(() => {
    router.push({
      pathname: Routes.SPA_DAY_BOOKING,
      query: { id, bookingStep: 'availability' },
    });
  }, [router, id]);

  const crossSellItems = mergeCrossSellWithUpsell(
    spaPackage?.upSell,
    'spa-day',
    spaPackage?.crossSellObjects
  ) as SpaCrossSellEntry[];

  const { items: recommendationItems, targets: recommendationTargets } =
    mapSpaCrossSellToRecommendations(crossSellItems, { fallbackImageUrl });

  const handleSelectRecommendation = useCallback(
    (recId: string) => {
      const target = recommendationTargets.get(recId);
      if (target) {
        router.push({ pathname: target.route, query: { id: target.id } });
      }
    },
    [router, recommendationTargets]
  );

  const handleBack = useCallback(() => {
    const isCategoryView = spaSettings?.isSpaDayCategoryViewVisible;
    const categoryId = spaPackage?.categories?.[0]?.id;

    if (isCategoryView && categoryId) {
      router.push({
        pathname: Routes.SPA_DAY_CATEGORY_DETAIL,
        query: { id: categoryId },
      });
    } else {
      router.push({
        pathname: Routes.SPA_DAY_LIST,
        query: { arrivalDate: parseDateToString(journeyParams?.spaDate) },
      });
    }
  }, [router, spaSettings, spaPackage, journeyParams?.spaDate]);

  const handleViewAllSpaDays = useCallback(() => {
    router.push({
      pathname: Routes.SPA_DAY_LIST,
      query: { arrivalDate: parseDateToString(journeyParams?.spaDate) },
    });
  }, [router, journeyParams?.spaDate]);

  const handleEditSearch = useCallback(() => {
    openJourneyPickerDates();
  }, []);

  const recommendations =
    recommendationItems.length > 0 ? (
      <Recommendations
        items={recommendationItems}
        imageAspectRatio={cardAspectRatio.square.desktop}
        imageMobileAspectRatio={cardAspectRatio.square.mobile}
        onSelect={handleSelectRecommendation}
        title={t('product.otherProducts.title')}
        fromPriceLabel={t('common.from')}
        noImageLabel={t('spa.noImage')}
      />
    ) : undefined;

  const termsContent =
    spaPackage?.termsAndConditions || spaSettings?.termsAndConditions ? (
      <>
        {spaPackage?.termsAndConditions && <ContentParser text={spaPackage.termsAndConditions} />}
        {spaSettings?.termsAndConditions && <ContentParser text={spaSettings.termsAndConditions} />}
      </>
    ) : undefined;

  const propertyModules = Object.keys(property.modulesExcludingDisabled);
  const hideArrivalTimes = spaSettings?.hideArrivalTimes || false;

  const discount = mapDiscountToDisplayObject(spaPackage?.discount);

  const promotionName = spaPackage?.discount?.name;

  // One place decides which notice this page shows: "we could not check" when the read failed,
  // the sold-out copy only when we actually have an answer to report.
  const unavailableFormattedDate = moment(selectedDateString).format('dddd Do MMMM YYYY');
  const unavailableNotice = !isUnavailable
    ? undefined
    : integrationUnavailable
    ? {
        title: t('spa.package.integrationUnavailableHeading'),
        subtitle: t('spa.package.integrationUnavailableDescription', {
          title: entity?.name,
          date: unavailableFormattedDate,
        }),
        ctaLabel: t('common.tryAgain'),
        onCtaClick: () => {
          void refetchTimeslots();
        },
      }
    : {
        title: t('spa.package.unavailableHeading'),
        subtitle: t('spa.package.unavailableDescription', {
          title: entity?.name,
          date: unavailableFormattedDate,
        }),
        ctaLabel: t('common.editSearch'),
        onCtaClick: handleEditSearch,
        secondaryCtaLabel: t('common.backToModuleRoot', { entity: t('app.spaDays') }),
        onSecondaryCtaClick: handleViewAllSpaDays,
      };

  return (
    <>
      {!bookingStep && (
        <>
          <Spinner showDelayMessage isActive={isRefetching} />
          <SpaDayDetailTemplate
            imageUrl={spaPackage?.image?.url ?? null}
            imageAlt={spaPackage?.name}
            title={spaPackage?.name ?? ''}
            price={spaPackage?.price}
            discount={discount}
            priceLabel={t('common.from')}
            promotionName={promotionName}
            categoryLabel={spaPackage?.categories?.[0]?.name}
            description={spaPackage?.description}
            termsContent={termsContent}
            termsLabel={t('homepage.termsAndConditions')}
            onReserve={
              isTelephoneOnly || isUnavailable || isLoadingTimeslots || isCheckingAvailability
                ? undefined
                : handleReserve
            }
            reserveLabel={t('spa.book')}
            onBack={handleBack}
            viewAllLabel={t('common.viewAll')}
            recommendations={recommendations}
            isLoading={isInitialLoading}
            journeyBar={journeyBar}
            telephoneOnly={
              isTelephoneOnly
                ? {
                    title: t('spa.telephoneOnly', {
                      spaEntity: t('spa.spaDay').toLowerCase(),
                    }),
                    subtitle: t('treatment.callUsOn', {
                      phoneNumber: spaSettings?.phoneNumber,
                    }),
                  }
                : undefined
            }
            unavailable={unavailableNotice}
          />
        </>
      )}
      {bookingStep === 'availability' && entity && (
        <FormProvider {...form}>
          <SpaDayBooking
            id={id}
            property={property}
            language={language}
            startDate={startDate}
            onClose={handleCloseModal}
            loggedInGuest={loggedInGuest}
          />
        </FormProvider>
      )}
      {bookingStep === 'extras' && entity && (
        <FormProvider {...form}>
          <SpaDayExtras
            extras={extras}
            entity={entity}
            property={property}
            language={language}
            id={id}
            onClose={handleCloseModal}
            onBack={() =>
              router.push({
                pathname: Routes.SPA_DAY_BOOKING,
                query: { id, bookingStep: 'availability' },
              })
            }
          />
        </FormProvider>
      )}
      <BasketDrawerVersion
        open={isBasketDrawerOpen}
        onClose={() => setIsBasketDrawerOpen(false)}
        propertyModules={propertyModules}
        hideArrivalTimes={hideArrivalTimes}
      />
    </>
  );
};
